import type { StoryObj } from '@storybook/html'
import type { CobaltDropdown as CobaltDropdownProps } from './cobalt-dropdown'

export default {
  title: 'Components/Action/cobalt-dropdown',
  argTypes: {
    alignment: {
      type: { name: 'string' },
      options: ['left', 'center', 'right'],
      control: { type: 'select' },
      description: 'Align the dropdown with the trigger element.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'center' }
      }
    },
    width: {
      type: { name: 'string' },
      options: ['small', 'medium', 'large'],
      control: { type: 'select' },
      description: 'Change the width of the dropdown.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'small' }
      }
    },
    cobaltDropdownItemsChange: {
      description: 'Custom event emitted when the selected values of the dropdown have changed.',
      table: {
        type: {
          summary: 'Integration',
          detail:
            'React: onCobaltDropdownItemsChange={handleChange}\nVue:@cobaltDropdownItemsChange="handleChange"\nAngular:(cobaltDropdownItemsChange)="handleChange()"\nVanillaJs:.addEventListener("cobaltDropdownItemsChange", (event) => {//handle change});'
        }
      },

      action: 'cobaltDropdownItemsChange'
    }
  }
}

const defaultItems = [
  { value: 'value1', label: 'value 1' },
  { value: 'value2', label: 'value 2' },
  { value: 'value2', label: 'value 2' },
  { value: 'value3', label: 'value 3', disabled: true }
]

const singleSelectionItems = [
  { value: 'value1', label: 'value 1', iconName: 'star-outline' },
  { value: 'value2', label: 'value 2', iconName: 'star-outline' },
  { value: 'value2', label: 'value 2', description: 'description', iconName: 'star-outline' },
  { value: 'value3', label: 'value 3', iconName: 'star-outline', disabled: true }
]

const multipleSelectionItems = [
  { value: 'value1', label: 'value 1' },
  { value: 'value2', label: 'value 2' },
  { value: 'value2', label: 'value 2', description: 'description' },
  { value: 'value3', label: 'value 3', disabled: true }
]

const DefaultTemplate = (args: CobaltDropdownProps) => {
  return `
<cobalt-dropdown alignment="${args.alignment}" width="${args.width}">
  <cobalt-dropdown-section format="selection" items='${JSON.stringify(defaultItems)}'>
  </cobalt-dropdown-section>
</cobalt-dropdown>
`
}

export const Default: StoryObj = DefaultTemplate.bind({})

Default.args = {
  alignment: 'center',
  width: 'small'
}

const SelectionTemplate = (args: CobaltDropdownProps) => {
  return `
<cobalt-dropdown alignment="${args.alignment}" width="${args.width}">
  <cobalt-dropdown-section format="selection" items='${JSON.stringify(singleSelectionItems)}'>
  </cobalt-dropdown-section>
</cobalt-dropdown>
`
}

export const Selection: StoryObj = SelectionTemplate.bind({})

Selection.args = {
  alignment: 'center',
  width: 'small'
}

const MultiSelectionTemplate = (args: CobaltDropdownProps) => {
  return `
<cobalt-dropdown alignment="${args.alignment}" width="${args.width}">
  <cobalt-dropdown-section format="multi-selection" items='${JSON.stringify(multipleSelectionItems)}'>
  </cobalt-dropdown-section>
</cobalt-dropdown>
`
}

export const MultiSelection: StoryObj = MultiSelectionTemplate.bind({})

MultiSelection.args = {
  alignment: 'center',
  width: 'small'
}

const ActionTemplate = (args: CobaltDropdownProps) => {
  return `
<script>
  document.getElementById('custom-action').addEventListener('cobaltDropdownActionItemClick', () => {
    alert('item clicked')
  })
</script>
<cobalt-dropdown alignment="${args.alignment}" width="${args.width}">
  <cobalt-dropdown-section format="action">
    <cobalt-dropdown-action-item id="custom-action" label="value1" icon-name="star-outline"></cobalt-dropdown-action-item>
    <cobalt-dropdown-action-item label="value2" icon-name="star-outline"></cobalt-dropdown-action-item>
    <cobalt-dropdown-action-item label="value3" icon-name="star-outline" disabled="true"></cobalt-dropdown-action-item>
  </cobalt-dropdown-section>
</cobalt-dropdown>
`
}

export const Action: StoryObj = ActionTemplate.bind({})

Action.args = {
  alignment: 'center',
  width: 'small'
}

const CustomTriggerElementTemplate = (args: CobaltDropdownProps) => {
  return `
<cobalt-dropdown alignment="${args.alignment}" width="${args.width}">
  <cobalt-icon-button slot="trigger-element" variant="tertiary" label="opening options" icon-name="line_stack-outline"></cobalt-icon-button>
  <cobalt-dropdown-section format="selection" items='${JSON.stringify(singleSelectionItems)}'></cobalt-dropdown-section>
</cobalt-dropdown>
`
}

export const CustomTriggerElement: StoryObj = CustomTriggerElementTemplate.bind({})

CustomTriggerElement.args = {
  alignment: 'center',
  width: 'small'
}

const TemplateOnChangeEvent = (args: CobaltDropdownProps) => {
  return `
<script>
  document.querySelector('#dropdown-on-change').addEventListener('cobaltDropdownItemsChange', (e) => {
    document.querySelector('#selected-items').textContent = JSON.stringify(e.detail)
  })
</script>
<div style="display:flex; flex-direction: column; gap: var(--number-spacing-large);">
  <p class="typography-body-medium-regular">Values sent on change: <span id="selected-items">no event emitted yet</span></p>
  <cobalt-dropdown id="dropdown-on-change" alignment="${args.alignment}" width="${args.width}">
    <cobalt-dropdown-section format="selection" items='${JSON.stringify(singleSelectionItems)}'>
    </cobalt-dropdown-section>
  </cobalt-dropdown>
</div>
`
}

export const OnChangeEvent: StoryObj = TemplateOnChangeEvent.bind({})

OnChangeEvent.args = {
  alignment: 'center',
  width: 'small'
}

const MultipleSectionsTemplate = (args: CobaltDropdownProps) => {
  return `
<cobalt-dropdown alignment="${args.alignment}" width="${args.width}">
  <cobalt-dropdown-section format="selection" section-title="Section 1" description="Description section 1" items='${JSON.stringify(singleSelectionItems)}'>
  </cobalt-dropdown-section>
  <cobalt-dropdown-section format="selection" section-title="Section 2" description="Description section 2" divider items='${JSON.stringify(singleSelectionItems)}'>
  </cobalt-dropdown-section>
</cobalt-dropdown>
`
}

export const MultipleSections: StoryObj = MultipleSectionsTemplate.bind({})

MultipleSections.args = {
  alignment: 'center',
  width: 'small'
}
